/**
 * Constants for the loyalty flow.
 *
 * The display group (`TEXT_TRANSFORM` … `EXCLUSION_SCOPES`) came from
 * `apps/web/src/Containers/LoyaltyLeg/constants.ts` on 2026-09-04. That file was a re-export
 * shim and is **deleted (2026-09-09)** along with the screens that read it. They are program
 * vocabulary, not screen vocabulary —
 * every rule in `./program.ts` reads them, and the kiosk and mobile will too.
 *
 * The flow group below them was previously spelled inline in the components.
 */

/** How a collectible label should be cased. Argument to `applyTextTransform`. */
export const TEXT_TRANSFORM = {
  UPPERCASE: "uppercase",
  LOWERCASE: "lowercase",
  CAPITALIZE: "capitalize",
  FIRST_UPPER: "first_upper",
} as const;

/** What a program's `burnValue` percentage is a percentage OF. */
export const MAX_BURN_METHOD = {
  ORDER_PCT: "ORDER_PCT",
  BALANCE_PCT: "BALANCE_PCT",
};

export const BURN_METHOD_LABELS: Record<string, string> = {
  [MAX_BURN_METHOD.ORDER_PCT]: "order value",
  [MAX_BURN_METHOD.BALANCE_PCT]: "point balance",
};

/** Whether a minimum-order-value threshold is measured inclusive or exclusive of taxes. */
export const MOV_TYPE_LABELS: Record<string, string> = {
  final: "Incl. taxes",
  gross: "Excl. taxes",
};

export const EXCLUSION_TYPES = {
  CATEGORY: 1,
  SUB_CATEGORY: 2,
  ITEM: 3,
} as const;

export const EXCLUSION_SCOPES = {
  EARN: "earn",
  BURN: "burn",
  BOTH: "both",
} as const;

/**
 * How often points may be earned — a program's `cooloffType`.
 *
 * Read by `getCooloffText` in `./programDisplay`. Named here rather than spelled inline in the
 * view, because `HOURS` with a value of `0` means NO cool-off and has to be phrased as such.
 */
export const COOLOFF_TYPES = {
  HOURS: "hours",
  CALENDAR_DAY: "calendar_day",
} as const;

/** What decides whether a points multiplier is active — a multiplier's `ruleType`. */
export const MULTIPLIER_RULE_TYPES = {
  /** Runs on given days, in given time slots. `config` carries `days` + `time_slots`. */
  DAY_TIME: "day_time",
  /** Runs on given categories/items. `config` is the list. */
  CATEGORY_ITEM: "category_item",
} as const;

/**
 * What one row of the customer's points ledger records — `history[].type`.
 *
 * `BURN` and `EXPIRE` both SUBTRACT points and are not interchangeable: only an expiry is
 * shaded and carries an `expiresAt`. Anything else is an accrual.
 */
export const HISTORY_TYPES = {
  BURN: "burn",
  EXPIRE: "expire",
} as const;

/** A bonus's `bonusType`. Only the welcome bonus is phrased differently. */
export const BONUS_TYPES = {
  WELCOME: "welcome",
} as const;

/**
 * How the welcome bonus appears in the CUSTOMER's ledger (`loyaltyInfo.bonus[]`).
 *
 * ⚠️ Deliberately not the same vocabulary as `BONUS_TYPES`: that is how the PROGRAMME declares a
 * bonus it offers, this is how the customer's history records having received one. A ledger row
 * is `{ type: "bonus", reason: "welcome_bonus" }` and both halves must match — see
 * `isWelcomeBonusEarned`.
 */
export const BONUS_LEDGER_TYPE = "bonus";
export const WELCOME_BONUS_REASON = "welcome_bonus";

/**
 * How a programme hands value back — `redemptionType`.
 *
 * 🔴 **The two are mutually exclusive surfaces.** `INR` is the redemption panel (points come off
 * as money); `RISTA_DEAL` is the milestone reward rows (points unlock named rewards, each applied
 * as a deal). A terminal that shows both offers the same balance twice — see `./rewards`.
 *
 * ⚠️ Spelled on BOTH the programme envelope and the customer's `loyaltyInfo`, by the provider,
 * and neither is derived from the other. `isPointsRedemption` reads the first,
 * `isRewardRedemption` the second.
 */
export const REDEMPTION_TYPES = {
  INR: "inr",
  RISTA_DEAL: "rista_deal",
} as const;

/**
 * Reward validity thresholds.
 *
 * `SOON_THRESHOLD_DAYS` is what turns the row's footer into the "expiring in N days" warning —
 * a display rule, so it lives beside the copy it drives rather than inline in the view.
 */
export const REWARD_EXPIRY = {
  SOON_THRESHOLD_DAYS: 3,
  MS_PER_DAY: 1000 * 60 * 60 * 24,
  /** A deal's `status` when the merchant has it live. Anything else means paused/unlinked. */
  ACTIVE_STATUS: "Active",
};

/** Fallback when a program does not name its collectible. */
export const DEFAULT_COLLECTIBLE_NAME = "Point";

/** Seconds before a "Resend OTP" becomes available. Same for both OTP flows. */
export const OTP_RESEND_SECONDS = 30;

/**
 * OTP lengths.
 *
 * ⚠️ The redemption OTP's length depends on the PROVIDER: the program-driven partner sends 4
 * digits, everyone else 6. `MIN` is what enables the Verify button — the old component accepted
 * 4 digits regardless of which provider was active, and that is preserved rather than tightened,
 * because a partner sending a shorter code than advertised would otherwise become unverifiable.
 */
export const REDEEM_OTP = {
  PROGRAM_DRIVEN_LENGTH: 4,
  DEFAULT_LENGTH: 6,
  MIN_LENGTH: 4,
  /** The input stops accepting characters at this length (the app used `< 7`). */
  MAX_LENGTH: 6,
};

/** The enrolment-consent OTP is always 4 digits, and Verify needs exactly that many. */
export const CONSENT_OTP_LENGTH = 4;

/**
 * Backend `message` values the consent APIs answer with.
 *
 * ⚠️ These are matched as literal strings, exactly as the app did. `CUSTOMER_ALREADY_EXISTS` is
 * prose, not a code, so it is the more fragile of the two — but it is the only signal the
 * OTP-less path gets for "this customer is already enrolled", and dropping the check would turn
 * a success into an error toast.
 */
export const CONSENT_MESSAGES = {
  ALREADY_CONSENTED: "CUSTOMER_ALREADY_CONSENTED",
  CUSTOMER_EXISTS: "Customer Already Exists",
};

/**
 * What the panel says when a redemption fails without naming a reason.
 *
 * The common case, not an edge one: `commonPostData` swallows request errors and answers
 * `undefined`, so the failure carries no code for `getErrorMessage` to map. Before this existed
 * the panel showed nothing at all on a failed redeem.
 */
export const REDEMPTION_FAILED_KEY = "loyalty.redemptionFailed" as const;

/**
 * What the panel says when a RELEASE fails.
 *
 * 🔴 It has to name the consequence, not just the failure, because the panel's own state cannot.
 * A failed refund deliberately leaves the discount ON the bill — the backend still has those
 * points blocked — so every visible thing stays exactly as it was: the POS panel still reads
 * "applied", the kiosk's toggle stays ON. Nothing on screen is wrong, and nothing on screen has
 * changed, which is precisely why the message is the only thing that tells the customer their
 * total did not move.
 *
 * `loyaltyRefundHelper` also raises a toast, and that is NOT a substitute: on web every toast
 * shares one `toastId`, and `ToastNotifyError` additionally refuses to fire while any other
 * toast is up — so the message is dropped exactly when the kiosk is likeliest to be showing a
 * network toast already. Its wording is a cashier's, too.
 */
export const RELEASE_FAILED_KEY = "loyalty.releaseFailed" as const;

/**
 * What the OTP dialog says when a verify fails without naming a reason.
 *
 * Same root cause as `REDEMPTION_FAILED_KEY`: a swallowed request error reaches the callback
 * as `undefined`, so `getErrorMessage` has no code to map and answered `undefined` — which the
 * old `|| ""` turned into no message at all. A dialog that shows nothing after Confirm is a dead
 * end on a kiosk, where there is nobody to explain it.
 *
 * Worded so a customer knows the points are NOT yet confirmed and the dialog is still theirs to
 * act on — Confirm is live again, and so is Resend once the countdown clears.
 */
export const VERIFICATION_FAILED_KEY = "loyalty.verificationFailed" as const;

/**
 * What the payment list says above the modes a programme bars while points are on the bill.
 *
 * Copy, not a rule — but it belongs here for the same reason the rule does: it is printed by the
 * POS payment panel and the mobile one, and two surfaces describing the same restriction in two
 * different sentences is how a cashier learns to distrust one of them. See
 * `partitionPaymentMethodsForRedemption`.
 */
export const EXCLUDED_PAYMENT_MODES_NOTICE =
  "Applied loyalty points won't be used with below methods";

/**
 * What a cart line says when the programme bars paying for it with points.
 *
 * Same reasoning as `EXCLUDED_PAYMENT_MODES_NOTICE`: the answer comes from
 * `isItemExcludedFromRedemption`, and every cart that renders it should render it identically.
 */
export const ITEM_NOT_ELIGIBLE_FOR_REDEMPTION = "Not eligible for loyalty redemption";
